import { format } from "date-fns";
import { TableCell, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import UniversalStatusBadge from "@/components/shared/UniversalStatusBadge";
import { TeamMember } from "../hooks/useTeamData";
import TeamMemberActionsMenu from "./TeamMemberActionsMenu";

interface TeamMemberRowProps {
  member: TeamMember;
  currentUserId?: string;
  canManageMembers: boolean;
  showDeactivated: boolean;
  onMakeAdmin: (memberId: string, memberName: string) => void;
  onRemoveAdmin: (memberId: string, memberName: string) => void;
  onRemoveMember: (memberId: string, reason?: string) => void;
  onReactivateMember: (memberId: string) => void;
  onSuspendMember: (memberId: string, memberName: string) => void;
  onUnsuspendMember: (memberId: string, memberName: string) => void;
}

const TeamMemberRow = ({
  member,
  currentUserId,
  canManageMembers,
  showDeactivated,
  onMakeAdmin,
  onRemoveAdmin,
  onRemoveMember,
  onReactivateMember,
  onSuspendMember,
  onUnsuspendMember
}: TeamMemberRowProps) => {
  const isCurrentUser = member.id === currentUserId;
  const memberName = `${member.first_name || ''} ${member.last_name || ''}`.trim() || 'Unknown';

  const getStatusBadge = (status: string, suspendedUntil?: string | null) => {
    // For temporary suspension, use a custom label
    if (status === 'suspended' && suspendedUntil && new Date(suspendedUntil) > new Date()) {
      return (
        <UniversalStatusBadge 
          entityType="user" 
          status="suspended" 
          labelOverride="Suspended (Temp)"
          size="sm"
        />
      );
    }
    return (
      <UniversalStatusBadge 
        entityType="user" 
        status={status} 
        size="sm"
      />
    );
  };

  return (
    <TableRow className={member.status === 'suspended' || member.status === 'deactivated' ? 'opacity-60' : ''}>
      <TableCell className="font-medium">
        <div>
          <div className="font-medium">{memberName}</div>
          {isCurrentUser && (
            <div className="text-xs text-muted-foreground">(You)</div>
          )}
          {/* Mobile: Show role as subtext */}
          <div className="sm:hidden text-xs text-muted-foreground mt-0.5">
            {member.company_position || 'member'}
          </div>
        </div>
      </TableCell>
      <TableCell className="hidden sm:table-cell">
        <div className="text-sm">
          {member.email || <span className="text-muted-foreground">No email</span>}
        </div>
      </TableCell>
      <TableCell className="hidden md:table-cell">
        <Badge variant={member.company_position === 'admin' ? 'default' : 'secondary'}>
          {member.company_position || 'member'}
        </Badge>
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        <Badge variant={member.role === 'admin' ? 'default' : 'outline'}>
          {member.role || 'user'}
        </Badge>
      </TableCell>
      <TableCell>
        {getStatusBadge(member.status, member.suspended_until)}
        {member.status === 'suspended' && member.suspended_until && (
          <div className="text-xs text-muted-foreground mt-1">
            Until: {format(new Date(member.suspended_until), 'dd MMMM yyyy')}
          </div>
        )}
        {member.status === 'deactivated' && member.deactivated_at && (
          <div className="text-xs text-muted-foreground mt-1 md:hidden">
            {format(new Date(member.deactivated_at), 'dd MMMM yyyy')}
          </div>
        )}
      </TableCell>
      {showDeactivated && (
        <TableCell className="hidden md:table-cell">
          {member.status === 'deactivated' && (
            <div className="text-sm">
              <div className="text-muted-foreground">
                Date: {member.deactivated_at ? format(new Date(member.deactivated_at), 'dd MMMM yyyy') : 'N/A'}
              </div>
              {member.deactivation_reason && (
                <div className="text-muted-foreground mt-1">
                  Reason: {member.deactivation_reason}
                </div>
              )}
            </div>
          )}
        </TableCell>
      )}
      {canManageMembers && (
        <TableCell className="text-right">
          <TeamMemberActionsMenu
            member={member}
            isCurrentUser={isCurrentUser}
            onMakeAdmin={() => onMakeAdmin(member.id, memberName)}
            onRemoveAdmin={() => onRemoveAdmin(member.id, memberName)}
            onRemoveMember={() => onRemoveMember(member.id)}
            onReactivateMember={() => onReactivateMember(member.id)}
            onSuspendMember={() => onSuspendMember(member.id, memberName)}
            onUnsuspendMember={() => onUnsuspendMember(member.id, memberName)}
          />
        </TableCell>
      )}
    </TableRow>
  );
};

export default TeamMemberRow;
